/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { request } from '@mairistem/tools';
import { ModalEntites } from '@mairistem/entites';
import { Dimmer, Loader } from '@mairistem/composants';

import type { ApiListResponse } from '@mairistem/administres-utils';

import { baseUrlApp } from '@/routes';
import { useEntiteStore, useProduitDomaineStore } from '@/zustand';
import type { Entite } from '@/types';

interface EntiteZustandProviderProps {
  children: React.ReactNode;
}

const EntiteZustandProvider = (props: EntiteZustandProviderProps) => {
  const { children } = props;

  const selectedEntite: Entite | null = useEntiteStore(
    (state) => state.selectedEntite,
  );
  const selectEntite = useEntiteStore((state) => state.selectEntite);
  const setBlocked = useEntiteStore((state) => state.setBlocked);
  const refresh = useEntiteStore((state) => state.refresh);
  const setRefresh = useEntiteStore((state) => state.setRefresh);
  const produitDomaine = useProduitDomaineStore(
    (state) => state.produitDomaine,
  );
  console.log('selectedEntite: ', selectedEntite);
  console.log('produitDomaine: ', produitDomaine);

  const history = useHistory();

  const [openModal, setOpenModal] = React.useState(false);

  /**
   * L'entité vient-elle de changer ? La modale de `@mairistem/entites` appelle
   * `onSelect` puis `onClose` dans le même clic : c'est `handleClose`, qui
   * navigue, qui doit le savoir — l'état du store n'est pas encore à jour.
   */
  const entiteChangee = React.useRef(false);

  const handleSelect = React.useCallback(
    (entite: Entite) => {
      entiteChangee.current =
        !_.isNil(selectedEntite) &&
        selectedEntite.identifiant !== entite?.identifiant;

      setRefresh(false);
      selectEntite(entite);
    },
    [selectedEntite, selectEntite, setRefresh],
  );

  const handleClose = React.useCallback(() => {
    setRefresh(false);
    setOpenModal(false);

    // Un écran ouvert (acte, liste filtrée…) appartient à l'ancienne entité :
    // retour à l'accueil de la nouvelle, et non à l'écran précédent (AD-12974).
    if (entiteChangee.current) {
      entiteChangee.current = false;
      history.replace(baseUrlApp);
    } else {
      history.goBack();
    }
  }, [history, setRefresh]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need
  React.useEffect(() => {
    if (_.isNil(selectedEntite) && !_.isNil(produitDomaine)) {
      request
        .get<unknown, ApiListResponse<Entite>>(
          '/api/administres/etat-civil/entites',
        )
        .then(({ data }: ApiListResponse<Entite>) => {
          if (data.length === 1) {
            selectEntite(data[0]);
          } else {
            setOpenModal(true);
          }
        });
    }
  }, [produitDomaine]);

  React.useEffect(() => {
    if (_.isNil(selectedEntite)) {
      setBlocked(true);
    } else {
      setOpenModal(false);
      setBlocked(false);
    }
  }, [selectedEntite, setBlocked]);

  React.useEffect(() => {
    if (refresh) {
      setOpenModal(true);
    }
  }, [refresh]);

  // Ajoute le header X-Entite aux requêtes
  React.useEffect(() => {
    const xEntite = selectedEntite?.identifiant;

    const interceptor = request.interceptors.request.use((config) => {
      // eslint-disable-next-line no-param-reassign
      config.headers['X-Entite'] = xEntite;

      return config;
    });

    return () => {
      request.interceptors.request.eject(interceptor);
    };
  }, [selectedEntite]);
  return (
    <>
      {children}
      {!_.isNil(produitDomaine) && (
        <ModalEntites
          open={openModal}
          entiteSelected={selectedEntite}
          onSelect={handleSelect}
          onSave={() => null}
          onOpen={() => setOpenModal(true)}
          service="/api/administres/etat-civil/entites"
          // onClose={handleClose}
          {...(refresh && { onClose: handleClose })}
        />
      )}
      {_.isNil(selectedEntite) && (
        <Dimmer active inverted>
          <Loader size="large" />
        </Dimmer>
      )}
    </>
  );
};

export default EntiteZustandProvider;
